/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { CheckmarkIcon, CloseIcon, Text, type ThemeType } from "@packages/ui";
import * as Toast from "@radix-ui/react-toast";
import { AnimatePresence, motion } from "motion/react";
import { Fragment } from "react";

import { Durations } from "../lib/enums/durations";
import { ZIndices } from "../lib/enums/zIndices";
import { useContext } from "../Provider";

const cssToastViewport: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  backgroundColor: theme.shades.shade40,
  display: "flex",
  height: "100dvh",
  inset: 0,
  justifyContent: "center",
  margin: 0,
  padding: theme.spacing.l,
  position: "fixed",
  width: "100dvw",
  zIndex: ZIndices.Toasts,
});
const cssMotionLi: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  backgroundColor: theme.semanticColors.background,
  borderRadius: theme.radius.s,
  borderColor: theme.semanticColors.neutralsLowest,
  borderStyle: "solid",
  borderWidth: theme.stroke.light,
  boxShadow: `0 ${theme.dropShadow.s} ${theme.dropShadow.ms} 0 ${theme.shades.shade10}`,
  color: theme.semanticColors.primary,
  display: "flex",
  gap: theme.spacing.xxs,
  paddingBottom: theme.spacing.xxs,
  paddingLeft: theme.spacing.s,
  paddingRight: theme.spacing.s,
  paddingTop: theme.spacing.xxs,
  textAlign: "center",
});
export const Toasts = () => {
  const { toast } = useContext();

  return (
    <Toast.Provider>
      <AnimatePresence>
        <Fragment>
          {toast && (
            <Toast.Root asChild>
              <motion.li
                animate={{ opacity: 1, scale: 1 }}
                css={cssMotionLi}
                exit={{ opacity: 0, scale: 0.975 }}
                initial={{ opacity: 0, scale: 0.975 }}
                transition={{
                  duration: Durations.Short,
                  ease: "easeInOut",
                }}
              >
                {toast.variant === "error" ? (
                  <CloseIcon
                    size={16}
                    css={(theme: ThemeType) => ({
                      color: theme.semanticColors.errorError,
                    })}
                  />
                ) : (
                  <CheckmarkIcon.SimpleOn size={16} />
                )}
                <Toast.Title asChild>
                  <Text size="s" type="title">
                    {toast.message}
                  </Text>
                </Toast.Title>
              </motion.li>
            </Toast.Root>
          )}
        </Fragment>
      </AnimatePresence>
      <Toast.Viewport asChild>
        <motion.ol
          animate={
            toast
              ? { opacity: 1, pointerEvents: "auto" }
              : { opacity: 0, pointerEvents: "none" }
          }
          css={cssToastViewport}
          initial={{ opacity: 0, pointerEvents: "none" }}
          transition={{
            duration: Durations.Short,
          }}
        />
      </Toast.Viewport>
    </Toast.Provider>
  );
};
